import { useState, useEffect, FC } from 'react';
import styled from '@emotion/styled';
import CountUp from 'react-countup';
import P2 from '@base/P2/P2';
import P1 from '@base/P1/P1';
import H4 from '@base/H4/H4';
import Divider from '@base/Divider/Divider';
import textHelper from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, Fonts, breakpoints, mediaQuery } from '@styles/index';
import { subscriptionTypes } from '@constants/index';
import { ISubscriptionAmountInfoProps } from 'types/checkout';
import isEmpty from 'lodash/isEmpty';
import useCreditsControl from '@api/hooks/credits/useCreditsControl';
import ChevronRight from '@icon/ChevronRight';
import ChevronDown from '@icon/ChevronDown';
import { STORE_PROCESSOR } from './constants/index';

const InfoContainer = styled.div<{
  $mwebCheckoutV2: boolean;
  $amountsRow: boolean;
}>`
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_3B};
  box-shadow: ${boxShadowGenerator()};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    padding: 16px;
    margin-bottom: ${Spacings.SPACING_0B};
    ${({ $mwebCheckoutV2, $amountsRow }) => {
      if ($mwebCheckoutV2)
        return `
          padding: 0px;
          margin: 0px;
          padding-top: ${$amountsRow ? '16px' : '0px'};
          box-shadow: unset;
        `;
    }}
  }
`;

const AmountsInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${Spacings.SPACING_3B};
  p {
    max-width: 70%;
  }
`;

const SemiBoldH4 = styled(H4)`
  ${mediaQuery.uptoMobile} {
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const TotalAmountsInfo = styled(AmountsInfo)<{ $mwebCheckoutV2?: boolean }>`
  margin-top: ${Spacings.SPACING_4B};
  margin-bottom: 0;
  ${mediaQuery.uptoMobile} {
    align-items: ${({ $mwebCheckoutV2 }) =>
      $mwebCheckoutV2 ? 'center' : 'unset'};
  }
`;
const DividerWrapper = styled.div`
  margin-top: 16px;
  height: 12px;
  background-color: var(--color-base-0);
  width: 100vw;
  position: relative;
  left: -24px;

  @media only screen and (max-width: ${breakpoints.mobile}) {
    width: calc(100% + 32px);
    margin-left: -16px;
    margin-right: -16px;
    height: 8px;
    left: 0;
  }
`;

const InlineP2 = styled(P2)`
  display: inline;
`;

const StyP1 = styled(P1)`
  font-weight: ${Fonts.LIGHT};
`;

const Line = styled(Divider)`
  margin-top: ${Spacings.SPACING_4B};
`;

const TotalAmount = styled(CountUp)<{ $mwebCheckoutV2?: boolean }>`
  font-weight: ${Fonts.BOLD};
  font-size: ${Fonts.H4};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  margin: ${Spacings.SPACING_0B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 16px;
    font-weight: ${({ $mwebCheckoutV2 }) =>
      $mwebCheckoutV2 ? Fonts.SEMIBOLD : Fonts.BOLD};
  }
`;

const SubscriptionAmountInfo: FC<ISubscriptionAmountInfoProps> = ({
  subscription,
  creditsApplied,
  credits,
  referralDiscount,
  referralCode,
  isReferralValid,
  partPaymentStatus,
  cashResponse,
  setLoanInterestAmt,
  loanMeta,
  isLoanSelected,
  mwebCheckoutV2 = false,
  setFinalSubscriptionAmount = () => {},
  showFullBreakDown = true
}) => {
  const subscriptionValue =
    (cashResponse ? cashResponse.subscriptionDetails : subscription) || {};

  const { isCreditsEnabledOnPlatform } = useCreditsControl();
  const creditsAppliedFlag =
    (creditsApplied || cashResponse?.credits > 0) && isCreditsEnabledOnPlatform;
  const currency = textHelper.currencySymbolMap(subscriptionValue.currency);
  const subscriptionPriceBreakdown =
    subscriptionValue?.subscriptionPriceBreakdown;
  const taxBreakdown = creditsAppliedFlag
    ? subscriptionValue.taxBreakdownWithCredits
    : subscriptionValue.taxBreakdown;

  const maxRetailPrice = subscriptionValue?.maxRetailPrice;
  const showDiscountPercent =
    maxRetailPrice &&
    subscriptionValue.price &&
    maxRetailPrice !== subscriptionValue.price;
  const total =
    (partPaymentStatus?.isActivePartSet &&
      partPaymentStatus?.partPayments?.reduce((a, b) => {
        return {
          amount: a.amount + b.amount
        };
      }).amount) ||
    Math.round(taxBreakdown?.find((item) => item.label === 'Total')?.amount);

  const loanAllowMinAmt = loanMeta.minimumAmount;
  const loanDiscount = loanMeta.discountPercentageOff;
  const loanDiscountAmt =
    isLoanSelected && loanDiscount && total >= loanAllowMinAmt
      ? Math.round(loanDiscount * 0.01 * total)
      : 0;

  useEffect(() => {
    setLoanInterestAmt(loanDiscountAmt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanDiscountAmt]);

  // countup values
  const [startValue, setStartValue] = useState(total);
  const [endValue, setEndValue] = useState(total);
  const [showAccordian, setShowAccordian] = useState(!mwebCheckoutV2);
  const [showPriceBreakDown, setShowPriceBreakDown] = useState(false);
  useEffect(() => {
    if (total !== endValue) {
      setStartValue(endValue);
      setEndValue(total);
    }
  }, [total, endValue]);

  useEffect(() => {
    setFinalSubscriptionAmount(endValue);
  }, [endValue, setFinalSubscriptionAmount]);

  const subscriptionFee =
    subscriptionValue?.maxRetailPrice || Math.round(subscriptionValue.price);
  const subscriptionFeeText = `${currency}${textHelper.commas(
    subscriptionFee
  )}`;

  useEffect(() => {
    if (
      !isEmpty(subscriptionPriceBreakdown) &&
      subscriptionPriceBreakdown !== null
    ) {
      setShowPriceBreakDown(true);
    }
  }, [showPriceBreakDown, subscriptionPriceBreakdown]);

  const cashReferralDiscount = cashResponse?.subscriptionDetails?.referralCode;
  const isStore = subscription?.processorName === STORE_PROCESSOR;

  type amountsType = {
    id: string;
    title: string;
    value: string;
    color?: string;
  };
  type priceBreakdownArray = {
    id: string;
    label: string;
    amount: number;
  };
  let amounts: amountsType[] = [];
  let breakdownArray: priceBreakdownArray[] = [];
  if (isStore || subscription?.type === subscriptionTypes.STORE) {
    if (!mwebCheckoutV2 || (showFullBreakDown && !showPriceBreakDown)) {
      amounts = [
        {
          id: 'subscription_fee',
          title: mwebCheckoutV2 ? 'Price' : 'Item price',
          value: subscriptionFeeText
        }
      ];
    }
  } else if (!mwebCheckoutV2) {
    amounts = [
      {
        id: 'subscription_fee',
        title:
          subscription?.type === subscriptionTypes.PLATFORM
            ? 'Batch fee'
            : 'Subscription fee',
        value: subscriptionFeeText
      }
    ];
  }
  const calDiscountPercent = () => {
    const discount =
      ((maxRetailPrice - subscriptionValue.price) / maxRetailPrice) * 100;
    return Math.floor(discount);
  };
  if (showDiscountPercent) {
    amounts.push({
      id: 'discount-price',
      title: `Offer discount (${calDiscountPercent()}%)`,
      value: `- ₹${textHelper.commas(
        maxRetailPrice - subscriptionValue.price
      )}`,
      color: 'var(--color-i-green)'
    });
  }
  if (!isEmpty(subscriptionValue.upgradeRefund)) {
    amounts.push({
      id: 'refund',
      // keeping for future reference
      // title: `Refund from current subscription (${subscriptionValue.upgrade_refund.remaining_days} days remaining)`,
      title: 'Refund from current paid subscription',
      value: `- ${currency}${textHelper.commas(
        subscriptionValue.upgradeRefund.refundAmount
      )}`,
      color: 'var(--color-i-green)'
    });
  }

  if (
    creditsAppliedFlag ||
    (partPaymentStatus?.isActivePartSet &&
      partPaymentStatus?.subscription?.creditsUsed)
  ) {
    amounts.push({
      id: 'credits',
      title: 'Credits applied',
      value: `- ${currency}${textHelper.commas(
        (partPaymentStatus?.isActivePartSet &&
          partPaymentStatus?.subscription?.creditsUsed) ||
          credits
      )}`,
      color: 'var(--color-i-green)'
    });
  }

  let partPaymentReferalDiscount;
  if (partPaymentStatus?.isActivePartSet) {
    if (partPaymentStatus?.subscription?.creditsUsed) {
      partPaymentReferalDiscount = partPaymentStatus?.subscription?.referralCode
        ?.discountPercent
        ? (subscriptionFee - partPaymentStatus?.subscription?.creditsUsed) *
          (partPaymentStatus?.subscription?.referralCode?.discountPercent / 100)
        : 0;
    } else {
      partPaymentReferalDiscount = partPaymentStatus?.subscription?.referralCode
        ?.discountPercent
        ? subscriptionFee *
          (partPaymentStatus?.subscription?.referralCode?.discountPercent / 100)
        : 0;
    }
  }

  const primeUpgradeDiscountPercentage =
    subscriptionValue?.referral?.primeUpgradeDiscountPercentage;
  const subscriptionFeeNum = Number(subscriptionFee) || 0;
  const primeUpgradeDiscountAmt =
    primeUpgradeDiscountPercentage && Number(primeUpgradeDiscountPercentage) > 0
      ? Math.round(
          subscriptionFeeNum * (Number(primeUpgradeDiscountPercentage) / 100)
        )
      : 0;

  if (cashReferralDiscount?.discountPercent) {
    const discountAmount =
      cashResponse?.credits > 0
        ? Math.round(
            (subscriptionFee - cashResponse.credits) *
              (cashReferralDiscount.discountPercent / 100) || referralDiscount
          )
        : Math.round(
            subscriptionFee * (cashReferralDiscount.discountPercent / 100) ||
              referralDiscount
          );
    amounts.push({
      id: 'referrals',
      title:
        cashReferralDiscount?.code?.toUpperCase() || referralCode.toUpperCase(),
      value: `- ${currency}${textHelper.commas(discountAmount)}`,
      color: 'var(--color-i-green)'
    });
  } else if (
    (isReferralValid && referralCode && !!referralDiscount) ||
    (partPaymentStatus?.isActivePartSet &&
      partPaymentStatus?.subscription?.referralCode?.referralType)
  ) {
    const combinedReferralDiscount = Math.round(
      partPaymentReferalDiscount || referralDiscount
    );
    const referralOnlyAmount = Math.max(
      0,
      combinedReferralDiscount - primeUpgradeDiscountAmt
    );
    amounts.push({
      id: 'referrals',
      // eslint-disable-next-line react/prop-types
      title:
        partPaymentStatus?.subscription?.referralCode?.code?.toUpperCase() ||
        referralCode.toUpperCase(),
      value: `- ${currency}${textHelper.commas(referralOnlyAmount)}`,
      color: 'var(--color-i-green)'
    });
  }

  if (primeUpgradeDiscountAmt > 0) {
    amounts.push({
      id: 'prime-upgrade-discount',
      title: 'Prime Upgrade Benefit',
      value: `- ${currency}${textHelper.commas(primeUpgradeDiscountAmt)}`,
      color: 'var(--color-i-green)'
    });
  }

  if (loanDiscountAmt) {
    const loanDiscountAmtTxt = `${currency}${textHelper.commas(
      loanDiscountAmt
    )}`;
    amounts.push(
      {
        id: 'interest-lender',
        title: 'Interest by lender',
        value: loanDiscountAmtTxt
      },
      {
        id: 'interest-discount',
        title: 'Interest discount by Unacademy',
        value: `- ${loanDiscountAmtTxt}`,
        color: 'var(--color-i-green)'
      }
    );
  }
  /* amounts.push(
    {
      id: 'sgst',
      title: 'SGST',
      value: sgstText
    },
    {
      id: 'cgst',
      title: 'CGST',
      value: cgstText
    }
  ); */
  if (subscriptionPriceBreakdown) {
    // revert keys to human readable labels
    breakdownArray = Object.entries(subscriptionPriceBreakdown).map(
      ([key, value]) => {
        const formattedKey = key
          .replace(/([A-Z])/g, ' $1')
          .replace(/&/g, ' & ')
          .replace(/\s+/g, ' ')
          .replace(/\b\w/g, (s) => s.toUpperCase())
          .trim();
        return { id: key, label: formattedKey, amount: value as number };
      }
    );
  }
  return (
    <InfoContainer
      role="subscriptionInfo-subscriptionAmountInfoContainer"
      $mwebCheckoutV2={mwebCheckoutV2}
      $amountsRow={!!amounts?.length}
    >
      {amounts?.map((data) => (
        <AmountsInfo key={data.id} role={`amountInfo-${data.id}`}>
          <StyP1 color={data.color || 'var(--color-text-secondary)'}>
            {data.title}
          </StyP1>
          <StyP1 color={data.color || 'var(--color-text-secondary)'}>
            {data.value}
          </StyP1>
        </AmountsInfo>
      ))}

      {showPriceBreakDown && <DividerWrapper />}
      {!showPriceBreakDown && showFullBreakDown && <Line />}
      {(showFullBreakDown || showPriceBreakDown) && (
        <TotalAmountsInfo
          role="amountInfo-totalAmount"
          $mwebCheckoutV2={mwebCheckoutV2}
        >
          {mwebCheckoutV2 ? (
            <SemiBoldH4>Total (Incl taxes)</SemiBoldH4>
          ) : (
            <H4>
              Total
              <InlineP2> (Incl. of all taxes)</InlineP2>
            </H4>
          )}
          <div
            style={{
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <TotalAmount
              start={startValue}
              end={endValue}
              separator=","
              prefix={currency}
              duration={1}
              $mwebCheckoutV2={mwebCheckoutV2}
            />
            {showPriceBreakDown &&
              (showAccordian ? (
                <ChevronDown onClick={() => setShowAccordian(!showAccordian)} />
              ) : (
                <ChevronRight
                  onClick={() => setShowAccordian(!showAccordian)}
                  size="24px"
                  color="var(--color-i-black)"
                />
              ))}
          </div>
        </TotalAmountsInfo>
      )}
      {showAccordian && showPriceBreakDown && (
        <>
          <div style={{ marginTop: '16px', marginBottom: '-12px' }}>
            {breakdownArray?.map((data) => (
              <AmountsInfo key={data.id} role={`amountInfo-${data.id}`}>
                <StyP1 color="var(--color-text-secondary)">{data.label}</StyP1>
                <StyP1 color="var(--color-text-secondary)">
                  {currency}
                  {data.amount.toLocaleString('en-IN', {
                    maximumFractionDigits: 20
                  })}
                </StyP1>
              </AmountsInfo>
            ))}
          </div>
        </>
      )}
    </InfoContainer>
  );
};

export default SubscriptionAmountInfo;
